// aiChannelKeys.js

const normalizeKey = (value) => String(value || '').trim();

export function sanitizeAiChannelSegment(value = '') {
  return String(value || '')
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 400);
}

export function buildAiMemberKeysHash(userKey) {
  const uid = normalizeKey(userKey);
  return uid ? `ai:self:${uid}` : '';
}

/** Legacy / Feed session key: conv_ai_{base}_{user} */
export function buildAiFeedChannelKey(baseKey, userKey) {
  const ws = sanitizeAiChannelSegment(baseKey);
  const uid = sanitizeAiChannelSegment(userKey);
  if (!ws || !uid) return '';
  return `conv_ai_${ws}_${uid}`;
}

/** @deprecated Prefer buildAiFeedChannelKey — alias kept for callers. */
export function buildAiChannelKey(baseKey, userKey) {
  return buildAiFeedChannelKey(baseKey, userKey);
}

/** Chat session key: conv_ai_{base}_{user}_{sessionId} */
export function buildAiChatChannelKey(baseKey, userKey, sessionId) {
  const feedKey = buildAiFeedChannelKey(baseKey, userKey);
  const sid = sanitizeAiChannelSegment(sessionId);
  if (!feedKey || !sid) return '';
  return `${feedKey}_${sid}`;
}

export function isAiChannelKey(channelKey) {
  return String(channelKey || '').trim().startsWith('conv_ai_');
}

export function isAiFeedChannelKeyForUser(channelKey, baseKey, userKey) {
  const expected = buildAiFeedChannelKey(baseKey, userKey);
  return Boolean(expected) && expected === String(channelKey || '').trim();
}

/**
 * True for this user's Feed key or any chat session key under that Feed prefix.
 */
export function isAiChannelKeyForUser(channelKey, baseKey, userKey) {
  const key = String(channelKey || '').trim();
  if (!isAiChannelKey(key)) return false;

  const feedKey = buildAiFeedChannelKey(baseKey, userKey);
  if (!feedKey) return false;
  if (key === feedKey) return true;
  return key.startsWith(`${feedKey}_`);
}

export function isAiFeedSession(channel, baseKey, userKey) {
  if (!channel) return false;
  const kind = String(channel.sessionKind || '').trim();
  if (kind === 'feed') return true;
  if (kind === 'chat') return false;
  // Legacy docs without sessionKind: treat canonical Feed key as feed.
  return isAiFeedChannelKeyForUser(
    channel.key || channel.id,
    baseKey,
    userKey,
  );
}

export function isAiChatSession(channel, baseKey, userKey) {
  if (!channel) return false;
  const kind = String(channel.sessionKind || '').trim();
  if (kind === 'chat') return true;
  if (kind === 'feed') return false;
  const key = String(channel.key || channel.id || '').trim();
  if (!isAiChannelKeyForUser(key, baseKey, userKey)) return false;
  return !isAiFeedChannelKeyForUser(key, baseKey, userKey);
}

/** Chat drafts with no messages stay out of Recents. Feed always listed. */
export function isAiSessionVisibleInRecents(channel, baseKey, userKey) {
  if (!channel) return false;
  if (isAiFeedSession(channel, baseKey, userKey)) return true;
  if (!isAiChatSession(channel, baseKey, userKey)) return false;
  return Boolean(channel.lastMessageAt);
}

/** Aliases for shorter call-site names. */
export const buildAiSessionChannelKey = buildAiChatChannelKey;
export const isAiSession = isAiChatSession;
